import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import LinkVariant from 'src/Elements/Buttons/LinkVariant'
import { SeparatorLineWithText } from 'src/Elements/Separator'
import { ExtensionConnectionTypes, ExtensionState } from 'src/Libs/installation-extensions/types'
import { makeStyles } from 'tss-react/mui'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { sendMessageToReactNative } from '@e3dc-react/shell/Libs/react-native'
import { useIsWebviewMode } from 'src/Hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { useExtensionConnection } from 'src/Libs/installation-extensions/hooks/extensions'
import useInstallationExtensionData from 'src/Libs/installation-extensions/hooks/extensions/use-installation-extension-data'
import { DisconnectButton } from '../../general'
import useModals from 'src/Hooks/useModals'

const useStyles = makeStyles<{ hasConnectionUrlError: boolean; isDisabled?: boolean }>()((theme, props) => ({
  button: {
    borderRadius: theme.spacing(12),
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  disabledButton: {
    backgroundColor: theme.palette.grey.grey06,
    borderColor: theme.palette.grey.grey06,
    pointerEvents: 'none',
    textDecoration: 'none',
  },
  link: {
    padding: theme.spacing(1),
    cursor: 'pointer',
  },
  connectionLink: {
    color: props.hasConnectionUrlError ? 'inherit' : props.isDisabled ? theme.palette.grey.grey03 : 'inherit',
    cursor: 'pointer',
  },
}))

const REDIRECT_URL = 'https://tibber.com/de/stromtarif?modal=preisrechner'

interface FooterContentProps {
  extensionState?: ExtensionState
  hasActiveTariff?: boolean
}

/**
 * Tibber detail view's footer content. Allows to get an offer or to link an existing account.
 */
const FooterContent: React.FC<FooterContentProps> = ({ extensionState, hasActiveTariff }) => {
  const { t } = useCustomTranslation()
  const {
    createExtensionConnectionUrl: createTibberConnectionUrl,
    extensionConnectionUrl: tibberConnectionUrl,
    hasConnectionUrlError,
  } = useExtensionConnection(ExtensionConnectionTypes.Tibber)
  const { openTibberDeleteConnectionModal } = useModals()

  const isWebview = useIsWebviewMode()
  const installationId = useTypedSelector(state => state.storage.activeInstallation)?.toString() ?? ''
  const { isLoading, loadInstallationExtensionStates } = useInstallationExtensionData(installationId)
  const disabledButtonTooltip = 'installation-extensions.tibber.connection-button.disabled-tooltip'
  const connectionUrlErrorTooltip = 'installation-extensions.tibber.connection-creation.error'
  const { classes, cx } = useStyles({ hasConnectionUrlError, isDisabled: hasActiveTariff })

  useEffect(() => {
    if (!tibberConnectionUrl) createTibberConnectionUrl()
  }, [tibberConnectionUrl]) //eslint-disable-line

  const handleOnClick = useCallback((): void => {
    if (tibberConnectionUrl) {
      sendMessageToReactNative({ type: 'tibber-url', url: tibberConnectionUrl })
    }
  }, [tibberConnectionUrl])

  const handleDisconnectExtension = (): void => {
    openTibberDeleteConnectionModal({ loadInstallationExtensionStates })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
      {!extensionState && !isLoading && (
        <>
          <OptionalTooltipWrapper
            hasTooltip={hasConnectionUrlError || hasActiveTariff}
            titleKey={hasActiveTariff ? disabledButtonTooltip : connectionUrlErrorTooltip}
          >
            <Button
              className={hasActiveTariff ? classes.disabledButton : classes.button}
              onClick={isWebview ? handleOnClick : undefined}
              disabled={hasConnectionUrlError}
            >
              <LinkVariant
                type="link"
                link={!isWebview ? tibberConnectionUrl ?? '' : undefined}
                hasUnderline={false}
                textClassName={cx(classes.link, classes.connectionLink)}
                label={t('installation-extensions.tibber.details.link-text')}
                clickable={!hasConnectionUrlError}
              />
            </Button>
          </OptionalTooltipWrapper>
          <SeparatorLineWithText text={t('installation-extensions.tibber.details.separator-text')} />
          <LinkVariant
            type="link"
            hasUnderline={false}
            label={t('installation-extensions.tibber.details.button-text')}
            textClassName={cx(classes.link)}
            link={REDIRECT_URL}
          />
        </>
      )}
      <DisconnectButton isConnected={extensionState === 'active'} onClickLink={handleDisconnectExtension} />
    </Box>
  )
}

export default FooterContent
